'use client';

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { logger } from '@axon/ui/utils';

import { useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/stores/auth.store';

interface AuthContextValue {
  user: ReturnType<typeof useAuth>['user'];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: ReturnType<typeof useAuth>['login'];
  logout: ReturnType<typeof useAuth>['logout'];
  register: ReturnType<typeof useAuth>['register'];
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  canAccess: (resource: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { isAuthenticated, refreshSession } = auth;

  // Set up activity tracking
  useEffect(() => {
    const handleActivity = () => {
      if (isAuthenticated) {
        useAuthStore.getState().updateLastActivity();
      }
    };

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated]);

  // Handle visibility change (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        // Tab became visible, check if we need to refresh
        refreshSession().catch((error) =>
          logger.error('Session refresh failed on visibility change', error)
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, refreshSession]);

  const value: AuthContextValue = {
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    login: auth.login,
    logout: auth.logout,
    register: auth.register,
    hasPermission: auth.hasPermission,
    hasRole: auth.hasRole,
    canAccess: auth.canAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
